import { useMemo, useCallback } from "react"
import {
  Box,
  Divider,
  List,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from "@mui/material"
import { useDirectory } from "hooks"
import {
  CategoryFilterBar,
  CategoryFilterChip,
} from "components/CategoryFilterBar"
import { EmptyData } from "components/EmptyData"
import { useTranslation } from "providers/i18n"

import { VenueSelector } from "../../components/VenueSelector"
import { HorizontalScrollPane } from "components/HorizontalScrollPane"
import { LevelFilterBar, ShopListItem } from "components/domains/directory"
import { Head } from "components/Head"
import { DirectoryOccupantCard } from "../../components/domains/directory/DirectoryOccupantCard"
import { StoreIcon } from "../../components/icons"
import { getIcon } from "utils/icon"

export const Directory = () => {
  const { t } = useTranslation()

  const {
    handleChangeParentCategory,
    handleChangeSubCategory,
    handleChangeOrdinal,
    handleSelectLevelChange,
    changeVenueFilter,
    resetFilter,
    filteredOccupantsSorted,
    filteredLevels,
    parentCategoryWithOccupant,
    subCategoryWithActiveParentCategory,
    parentCategoryId,
    subCategoryId,
    ordinalFilter,
    venueFilter,
    levelOptions,
    venueOptions,
    defaultVenueFilterValue,
    ref,
    showSubCategoryFilter,
    venues,
    isMultiOrdinalVenue,
    isMultiVenueProject,
    locationVenueId,
  } = useDirectory()
  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.directory) || <StoreIcon />,
    [theme]
  )
  const styleOverrides = theme.components[`DirectoryPage`]?.styleOverrides || {}
  const {
    root,
    filterSection,
    scrollPane,
    gridContainer,
    gridItem,
    categoryFilterBar,
    subCategoryFilterBar,
    list,
    divider,
    buildingFilterSelect,
    levelFilterSelect,
    buildingWithLevelFilterSection,
    selectMenuItem,
    resetButton,
    emptyDataWrapper,
    venueSelector,
  } = styleOverrides

  const disableFloorSelect = venueFilter === defaultVenueFilterValue

  const handleChangeVenue = useCallback(
    (e) => {
      changeVenueFilter(e?.target?.value)
    },
    [changeVenueFilter]
  )
  return (
    <>
      <Head title={t("Directory")} />
      <Box ref={ref} sx={root}>
        <Box sx={filterSection}>
          <HorizontalScrollPane sx={scrollPane}>
            <CategoryFilterBar
              sx={categoryFilterBar}
              activeCategory={parentCategoryId}
              categories={parentCategoryWithOccupant}
              onChange={handleChangeParentCategory}
            />
          </HorizontalScrollPane>
          {showSubCategoryFilter && (
            <HorizontalScrollPane sx={scrollPane}>
              <CategoryFilterBar
                sx={subCategoryFilterBar}
                activeCategory={subCategoryId}
                categories={subCategoryWithActiveParentCategory}
                onChange={handleChangeSubCategory}
                renderFilterButton={CategoryFilterChip}
              />
            </HorizontalScrollPane>
          )}
          {!isMultiVenueProject && isMultiOrdinalVenue && (
            <HorizontalScrollPane sx={scrollPane}>
              <LevelFilterBar
                levels={filteredLevels}
                onChange={handleChangeOrdinal}
                value={ordinalFilter}
              />
            </HorizontalScrollPane>
          )}
          {/* Temporary Hide New Multi building filter */}
          {isMultiVenueProject && (
            <Box sx={buildingWithLevelFilterSection}>
              <FormControl sx={buildingFilterSelect}>
                <InputLabel>{t("Venue")}</InputLabel>
                <Select
                  onChange={handleChangeVenue}
                  label="venue"
                  value={venueFilter}
                >
                  {venueOptions.map(({ id, name, value }) => (
                    <MenuItem
                      value={value}
                      sx={selectMenuItem}
                      key={`venue-filter-${id}`}
                    >
                      {t(name)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isMultiOrdinalVenue && (
                <FormControl sx={levelFilterSelect}>
                  <InputLabel>{t("Floor")}</InputLabel>
                  <Select
                    label={"Floor"}
                    value={ordinalFilter}
                    onChange={handleSelectLevelChange}
                    disabled={disableFloorSelect}
                  >
                    {levelOptions.map(({ value, id, name }) => (
                      <MenuItem
                        value={value}
                        key={`select-floor-${id}`}
                        sx={selectMenuItem}
                      >
                        {t(name)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Button variant="text" onClick={resetFilter} sx={resetButton}>
                {t("Clear")}
              </Button>
            </Box>
          )}
          <Divider sx={divider} />
        </Box>
        <List sx={list}>
          {filteredOccupantsSorted?.length > 0 ? (
            filteredOccupantsSorted.map((occupant) => (
              <ShopListItem
                key={`directory-item-${occupant.id}`}
                occupant={occupant}
                hideLevel={!isMultiOrdinalVenue}
              />
            ))
          ) : (
            <Box sx={emptyDataWrapper}>
              <EmptyData
                icon={emptyIcon}
                primary={t("Coming soon")}
                secondary={t("Stay tuned for the update")}
              />
            </Box>
          )}
        </List>
        <Box sx={gridContainer}>
          {filteredOccupantsSorted?.length > 0 ? (
            filteredOccupantsSorted.map((occupant) => (
              <Box sx={gridItem} key={`directory-item-${occupant.id}`}>
                <DirectoryOccupantCard data={occupant} />
              </Box>
            ))
          ) : (
            <Box sx={emptyDataWrapper}>
              <EmptyData
                icon={emptyIcon}
                primary={t("Coming soon")}
                secondary={t("Stay tuned for the update")}
              />
            </Box>
          )}
        </Box>
        {isMultiVenueProject && (
          <Box sx={venueSelector}>
            <VenueSelector
              selectedVenue={venueFilter}
              venues={venues}
              onClickVenue={changeVenueFilter}
              currentVenue={locationVenueId}
            />
          </Box>
        )}
      </Box>
    </>
  )
}
